import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type Interpolation, type Theme } from '@emotion/react';
import { ToggleButton, useTheme as useMuiTheme } from '@mui/material';
import { useFileDialog } from '@realadvisor/hooks';
import { CirclePlus, ImageIcon } from 'lucide-react';
import Quill from 'quill';

import { ComposerToolbar } from '@modules/_shared/components/ComposerToolbar';
import { matchWordListParagraph } from '@modules/_shared/components/quillClipboardMatchers';
import {
  qlEditorBaseStyles,
  qlEditorSurfaceStyles,
  qlListStyles,
} from '@modules/_shared/components/quillStyles';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import {
  type QuillClipboardMatcher,
  useQuillEditor,
} from '@modules/_shared/components/useQuillEditor';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

import { RichEditorValue } from './htmlValue';
import { MentionDropdown } from './MentionDropdown';
import {
  CustomHtmlBlot,
  ImagePlaceholderBlot,
  SNIPPET_REMOVE_CLASS,
  SignatureBlot,
  SnippetBlot,
  type SnippetRemoveHandler,
  VarBlot,
  getVarDisplay,
  refreshBrochureLabels,
  refreshEditorPreviews,
  registerQuillBlots,
} from './quillBlots';
import {
  canonicalHtmlFromQuill,
  collapseSingleLineBreaks,
  missingGuardIndexes,
} from './quillDocument';
import type {
  EmailEditorRenderSnippetProps,
  EmailEditorSnippetData,
  EmailEditorSuggestion,
} from './types';
import { appendUTM, removeUTM } from './utm';

export type SuggestionsType = EmailEditorSuggestion[];
export type LotSnippetData = EmailEditorSnippetData;

export type RichEditorProps = {
  placeholder?: string;
  className?: string;
  css?: Interpolation<Theme>;
  value: RichEditorValue;
  onChange: (value: RichEditorValue) => void;
  suggestions?: SuggestionsType;
  singleLine?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  errorText?: string | null;
  disabled?: boolean;
  onImageUpload?: (file: File) => Promise<string | null>;
  // +1 when an image's loading box appears, -1 once it is gone. The box
  // serializes to nothing, so a host sums these and holds Send/Save while > 0.
  // A delta, not a total: a host that remounts the editor (`key`) keeps
  // counting the old instance's uploads correctly.
  onPendingImageDelta?: (delta: 1 | -1) => void;
  // Property/lot snippets only make sense where the email has a listing
  // context - contexts like the signature editor turn the button off.
  showSnippetButton?: boolean;
  snippetLotId?: string | null;
  snippetDevelopmentId?: string | null;
  // A ready-built snippet for a listing the lot/development pickers can't
  // load (e.g. another tenant's MLS lot); the dialog offers only this one.
  presetSnippet?: EmailEditorSnippetData | null;
  snippetPreviewLanguage?: string;
  snippetPreviewT?: EmailEditorRenderSnippetProps['t'];
  pinSignatureToBottom?: boolean;
};

registerQuillBlots();

const importSnippetDialog = () =>
  import('@modules/_shared/components/email-form/InsertSnippetDialog').then(
    m => ({ default: m.InsertSnippetDialog }),
  );

let snippetDialogPreloaded = false;
const preloadSnippetDialog = (): void => {
  if (snippetDialogPreloaded) {
    return;
  }
  snippetDialogPreloaded = true;
  void importSnippetDialog();
};

const InsertSnippetDialog = lazy(importSnippetDialog);

const EDITOR_FORMATS = [
  'bold',
  'italic',
  'indent',
  'list',
  'link',
  'image',
  SnippetBlot.blotName,
  VarBlot.blotName,
  ImagePlaceholderBlot.blotName,
  CustomHtmlBlot.blotName,
  SignatureBlot.blotName,
];

// An editor accepts images only if it can upload them: without a pipeline the
// `image` format is off, so a pasted `<img>` or base64 image cannot slip in.
// Read-only editors keep it to display the images already in the content.
const EDITOR_FORMATS_WITHOUT_IMAGES = EDITOR_FORMATS.filter(
  format => format !== 'image',
);

// Quill's Uploader only calls `mimetypes.includes(file.type)`, so a predicate
// accepts every `image/*` type, matching the toolbar button's picker.
const IMAGE_MIMETYPES = {
  includes: (type: string) => type.startsWith('image/'),
};

// Assigned on the live module, not through the module config: Quill merges
// options with lodash `merge`, which grafts `includes` onto the default
// `['image/png', 'image/jpeg']` array instead of replacing it.
const applyImageMimetypes = (editor: Quill): void => {
  const uploader = editor.getModule('uploader') as
    | { options: { mimetypes: unknown } }
    | undefined;
  if (uploader == null) {
    console.error(
      'QuillRichEditor: uploader module missing, image drop/paste disabled',
    );
    return;
  }
  uploader.options.mimetypes = IMAGE_MIMETYPES;
};

const LINE_BLOCK_EMBEDS = new Set<string>([
  SnippetBlot.blotName,
  CustomHtmlBlot.blotName,
  SignatureBlot.blotName,
]);

const insertsSnippet = (ops: any[]): boolean =>
  ops.some(
    op =>
      op.insert != null &&
      typeof op.insert === 'object' &&
      SnippetBlot.blotName in op.insert,
  );

const makeSnippetRemover = (
  editor: Quill,
): SnippetRemoveHandler | undefined => {
  if (!editor.isEnabled()) {
    return undefined;
  }
  return node => {
    // A blot detached from the document offsets to 0, which would delete the
    // first character of the body instead of the card.
    if (!editor.root.contains(node)) {
      return;
    }
    const blot = Quill.find(node);
    if (blot == null || blot instanceof Quill) {
      return;
    }
    editor.deleteText(editor.getIndex(blot), 1, 'user');
  };
};

// A click past the end of a line that ends with a variable (or an image upload
// placeholder) puts the caret inside it, which is `contenteditable=false`: the
// browser drops what is typed there. Move it beside, as Quill would place it.
const moveCaretOutOfChip = (editor: Quill): void => {
  const selection = document.getSelection();
  const anchor = selection?.anchorNode;
  if (
    !editor.isEnabled() ||
    selection == null ||
    !selection.isCollapsed ||
    anchor == null
  ) {
    return;
  }
  const element = anchor instanceof Element ? anchor : anchor.parentElement;
  const chip = element?.closest<HTMLElement>(
    `.${VarBlot.className}, .${ImagePlaceholderBlot.className}`,
  );
  if (chip == null || !editor.root.contains(chip)) {
    return;
  }
  const blot = Quill.find(chip);
  if (blot == null || blot instanceof Quill) {
    return;
  }
  const before =
    anchor === chip.firstChild ||
    (selection.anchorOffset === 0 && anchor !== chip.lastChild);
  editor.setSelection(editor.getIndex(blot) + (before ? 0 : 1), 0, 'user');
};

const isEditorBlank = (editor: Quill): boolean =>
  editor.getText().trim() === '' &&
  !editor
    .getContents()
    .ops.some(op => op.insert != null && typeof op.insert === 'object');

const makeEmbedMatcher =
  (attr: string, blotName: string) =>
  (node: HTMLElement, _delta: unknown): any => {
    const Delta = Quill.import('delta') as any;
    return new Delta().insert({ [blotName]: node.getAttribute(attr) ?? '' });
  };

const matchSnippet = makeEmbedMatcher('data-ra-snippet', SnippetBlot.blotName);
const matchCustomHtml = makeEmbedMatcher(
  'data-ra-custom-html',
  CustomHtmlBlot.blotName,
);
const matchSignature = makeEmbedMatcher(
  'data-ra-signature',
  SignatureBlot.blotName,
);

const matchVar = (node: HTMLElement, _delta: unknown): any => {
  const Delta = Quill.import('delta') as any;
  const id = node.getAttribute('data-ra-var') ?? '';
  return new Delta().insert({
    [VarBlot.blotName]: { id, display: getVarDisplay(node) },
  });
};

const QUILL_MATCHERS: ReadonlyArray<readonly [string, QuillClipboardMatcher]> =
  [
    ['div[data-ra-snippet]', matchSnippet],
    ['div[data-ra-custom-html]', matchCustomHtml],
    ['div[data-ra-signature]', matchSignature],
    ['span[data-ra-var]', matchVar],
    ['p[class*="MsoListParagraph"]', matchWordListParagraph],
  ];

const SnippetButton = ({
  quill,
  disabled,
  snippetLotId,
  snippetDevelopmentId,
  presetSnippet,
}: {
  quill: Quill | null;
  disabled: boolean;
  snippetLotId?: string | null;
  snippetDevelopmentId?: string | null;
  presetSnippet?: EmailEditorSnippetData | null;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const savedRangeRef = useRef<{ index: number; length: number } | null>(null);
  const lastRangeRef = useRef<{ index: number; length: number } | null>(null);

  useEffect(() => {
    if (quill == null) {
      return;
    }
    const rememberSelection = (
      range: { index: number; length: number } | null,
    ) => {
      if (range != null) {
        lastRangeRef.current = range;
      }
    };
    rememberSelection(quill.getSelection());
    quill.on('selection-change', rememberSelection);
    return () => {
      quill.off('selection-change', rememberSelection);
    };
  }, [quill]);

  const insertSnippet = (data: EmailEditorSnippetData) => {
    if (quill == null) {
      return;
    }
    const range =
      savedRangeRef.current ?? quill.getSelection() ?? lastRangeRef.current;
    const index = range?.index ?? 0;
    const insertedAtStart = index === 0;
    const [line, lineOffset] = quill.getLine(index);
    const insertedAtLineEnd = line != null && lineOffset === line.length() - 1;
    quill.insertEmbed(
      index,
      SnippetBlot.blotName,
      encodeURIComponent(JSON.stringify(data)),
      'user',
    );
    const afterSnippetIndex = index + (insertedAtStart ? 2 : 1);
    if (insertedAtLineEnd && quill.getText(afterSnippetIndex, 1) === '\n') {
      quill.deleteText(afterSnippetIndex, 1, 'user');
    }
    quill.setSelection(afterSnippetIndex, 0, 'user');
    savedRangeRef.current = null;
  };

  return (
    <>
      <ToggleButton
        tabIndex={-1}
        value="snippet"
        selected={false}
        disabled={disabled}
        onMouseEnter={preloadSnippetDialog}
        onMouseDown={event => {
          event.preventDefault();
          savedRangeRef.current = quill?.getSelection() ?? lastRangeRef.current;
          setAnchorEl(event.currentTarget);
        }}
      >
        <CirclePlus size={18} data-testid="snippet-icon" />
      </ToggleButton>
      {anchorEl != null && (
        <Suspense fallback={null}>
          <InsertSnippetDialog
            initialLotId={snippetLotId}
            initialDevelopmentId={snippetDevelopmentId}
            presetSnippet={presetSnippet}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            onInsert={insertSnippet}
          />
        </Suspense>
      )}
    </>
  );
};

// Query params are signed-upload noise; the hosted origin+pathname is the
// canonical embedded form.
const canonicalImageUrl = (url: string): string => {
  const { origin, pathname } = new URL(url);
  return `${origin}${pathname}`;
};

// Live document index of a placeholder blot by id, or null if it's gone (the
// user deleted it, or the editor was cleared, before the upload resolved).
const placeholderIndex = (quill: Quill, id: string): number | null => {
  const node = quill.root.querySelector(`[data-ra-placeholder="${id}"]`);
  if (node == null) {
    return null;
  }
  const blot = Quill.find(node, true);
  if (blot == null || blot instanceof Quill) {
    return null;
  }
  try {
    return quill.getIndex(blot);
  } catch {
    return null;
  }
};

let placeholderSeq = 0;

// The placeholder -> image swap must not be its own undo entry: past the
// history merge window, Ctrl+Z would restore a placeholder whose upload has
// settled, a spinner nothing resolves. Only the successful swap is suppressed
// because it is length-neutral, so the indexes already on the undo stack stay
// valid; the removal paths shift content and must stay recorded.
const updateWithoutHistory = (quill: Quill, delta: any): void => {
  const history = quill.getModule('history') as
    | { ignoreChange: boolean }
    | undefined;
  if (history == null) {
    quill.updateContents(delta, 'user');
    return;
  }
  history.ignoreChange = true;
  try {
    quill.updateContents(delta, 'user');
  } finally {
    history.ignoreChange = false;
  }
};

type ImageInsertion = {
  upload: (file: File) => Promise<string | null>;
  onError: () => void;
  // +1 when an image's loading box appears, -1 once it is gone (swapped,
  // removed or abandoned): hosts hold Send/Save meanwhile, since the box
  // serializes to nothing.
  onPendingChange: (delta: 1 | -1) => void;
};

const IMAGE_PRELOAD_TIMEOUT_MS = 15_000;

// Resolves once the browser has the uploaded image (or gave up), so the box is
// swapped for an image that paints at once instead of an empty gap.
const preloadImage = (src: string): Promise<void> =>
  new Promise(resolve => {
    const image = new Image();
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, IMAGE_PRELOAD_TIMEOUT_MS);
    image.onload = done;
    image.onerror = done;
    image.src = src;
  });

// Each file gets a placeholder embed at its slot, so its position tracks the
// user's edits during the upload; once the image is loaded it is swapped in at
// the placeholder's *live* index. No setSelection: Quill transforms the caret,
// so typing meanwhile is safe.
const insertImages = (
  quill: Quill,
  startIndex: number,
  files: readonly File[],
  { upload, onError, onPendingChange }: ImageInsertion,
) => {
  const Delta = Quill.import('delta') as any;
  files.forEach((file, offset) => {
    const id = `ph-${(placeholderSeq += 1)}`;
    quill.insertEmbed(
      startIndex + offset,
      ImagePlaceholderBlot.blotName,
      id,
      'user',
    );
    onPendingChange(1);
    const removePlaceholder = () => {
      const index = placeholderIndex(quill, id);
      if (index != null) {
        quill.updateContents(new Delta().retain(index).delete(1), 'user');
      }
    };
    // Through a resolved promise so a handler that throws synchronously still
    // reaches the catch and the finally below.
    Promise.resolve()
      .then(() => upload(file))
      .then(async url => {
        if (url == null) {
          // `null` = no image, and the host reports its own errors (the
          // signature editor shows a snackbar): only a rejection reports here.
          removePlaceholder();
          return;
        }
        const src = canonicalImageUrl(url);
        await preloadImage(src);
        // The editor can be torn down meanwhile (a `disabled` flip remounts
        // Quill; a parent re-seed replaces the contents). The old root stays
        // query-able while detached, so guard on `isConnected` before writing,
        // otherwise the image would land in a dead document with no trace.
        if (!quill.root.isConnected) {
          console.warn(
            'QuillRichEditor: image upload resolved after the editor was torn down; dropping the result',
          );
          return;
        }
        const index = placeholderIndex(quill, id);
        if (index == null) {
          console.warn(
            'QuillRichEditor: image upload resolved after its placeholder was removed',
          );
          return;
        }
        updateWithoutHistory(
          quill,
          new Delta().retain(index).delete(1).insert({ image: src }),
        );
      })
      .catch(error => {
        removePlaceholder();
        console.error('QuillRichEditor: image upload failed', error);
        onError();
      })
      .finally(() => onPendingChange(-1));
  });
};

const ImageButton = ({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick: (file: File) => void;
}) => {
  const openFileDialog = useFileDialog({
    accept: 'image/*',
    multiple: false,
    onChange: files => {
      const [file] = files;
      if (file != null) {
        onPick(file);
      }
    },
  });

  return (
    <ToggleButton
      tabIndex={-1}
      value="image"
      selected={false}
      disabled={disabled}
      onClick={openFileDialog}
    >
      <ImageIcon size={18} data-testid="image-icon" />
    </ToggleButton>
  );
};

const Toolbar = ({
  quill,
  disabled,
  onPickImage,
  showSnippetButton = true,
  snippetLotId,
  snippetDevelopmentId,
  presetSnippet,
}: {
  quill: Quill | null;
  disabled: boolean;
  onPickImage?: (file: File) => void;
  showSnippetButton?: boolean;
  snippetLotId?: string | null;
  snippetDevelopmentId?: string | null;
  presetSnippet?: EmailEditorSnippetData | null;
}) => {
  useEffect(() => {
    if (!showSnippetButton) {
      return;
    }
    const id = setTimeout(preloadSnippetDialog, 0);
    return () => clearTimeout(id);
  }, [showSnippetButton]);

  return (
    <ComposerToolbar
      quill={quill}
      disabled={disabled}
      transformLinkForDisplay={removeUTM}
      transformLinkForSave={appendUTM}
    >
      {showSnippetButton && (
        <SnippetButton
          quill={quill}
          disabled={disabled}
          snippetLotId={snippetLotId}
          snippetDevelopmentId={snippetDevelopmentId}
          presetSnippet={presetSnippet}
        />
      )}
      {onPickImage != null && (
        <ImageButton disabled={disabled} onPick={onPickImage} />
      )}
    </ComposerToolbar>
  );
};

export const RichEditor = ({
  placeholder,
  className,
  css,
  value,
  onChange,
  singleLine = false,
  autoFocus = false,
  disabled = false,
  errorText: _errorText,
  onImageUpload,
  onPendingImageDelta,
  showSnippetButton = true,
  snippetLotId,
  snippetDevelopmentId,
  presetSnippet,
  snippetPreviewLanguage,
  snippetPreviewT,
  suggestions,
  required: _required,
  pinSignatureToBottom = false,
}: RichEditorProps) => {
  const muiTheme = useMuiTheme();

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const singleLineRef = useRef(singleLine);
  singleLineRef.current = singleLine;
  const snippetPreviewTRef = useRef(snippetPreviewT);
  snippetPreviewTRef.current = snippetPreviewT;
  const snippetPreviewLanguageRef = useRef(snippetPreviewLanguage);
  snippetPreviewLanguageRef.current = snippetPreviewLanguage;

  const refreshPreviews = useCallback((editor: Quill) => {
    refreshEditorPreviews(
      editor.root,
      snippetPreviewTRef.current,
      snippetPreviewLanguageRef.current,
      makeSnippetRemover(editor),
    );
  }, []);

  const seedEditor = useCallback(
    (editor: Quill, html: string): boolean => {
      const Delta = Quill.import('delta') as any;
      const converted = editor.clipboard.convert({ html });
      let delta =
        converted.length() === 0 ? new Delta().insert('\n') : converted;
      // Back to front, so the earlier indexes stay valid as paragraphs go in.
      for (const at of missingGuardIndexes(
        delta.ops,
        LINE_BLOCK_EMBEDS,
      ).reverse()) {
        delta = delta.compose(new Delta().retain(at).insert('\n'));
      }
      // A value replaced while the agent types in this editor (variables
      // refreshed by the composer) keeps the caret where it was. Focus, not
      // the page selection: a selection left behind by a click on a button
      // must not pull focus back here.
      const selection = editor.hasFocus() ? editor.getSelection() : null;
      editor.setContents(delta, 'silent');
      if (selection != null) {
        editor.setSelection(
          Math.min(selection.index, editor.getLength() - 1),
          0,
          'silent',
        );
      }
      refreshPreviews(editor);
      return singleLineRef.current && collapseSingleLineBreaks(editor);
    },
    [refreshPreviews],
  );

  const handleChange = useCallback((html: string) => {
    onChangeRef.current(new RichEditorValue(html));
  }, []);

  const handleTextChange = useCallback(
    (editor: Quill, ops: any[], _source: string): boolean => {
      if (insertsSnippet(ops)) {
        refreshPreviews(editor);
      } else if (ops.some(op => op.insert?.[VarBlot.blotName])) {
        // Labels only: re-rendering the property cards rewrites their DOM
        // under Quill, and the text typed after the new variable never lands.
        refreshBrochureLabels(editor.root, snippetPreviewTRef.current);
      }
      const { ops: contents } = editor.getContents();
      const [gap] = missingGuardIndexes(contents, LINE_BLOCK_EMBEDS);
      if (gap != null) {
        editor.insertText(gap, '\n', 'user');
        if (gap === 0) {
          editor.setSelection(0, 0, 'user');
        }
        // One gap per change: the insert re-enters here for the next one.
        return true;
      }
      if (singleLineRef.current) {
        collapseSingleLineBreaks(editor);
      }
      return false;
    },
    [refreshPreviews],
  );

  const configureEditor = useCallback((editor: Quill) => {
    editor.keyboard.addBinding({ key: 'a', shortKey: true }, () => {
      editor.setSelection(0, editor.getLength(), 'user');
      return false;
    });
    applyImageMimetypes(editor);
  }, []);

  const { t } = useLocale();

  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const showUploadError = () =>
    setImageUploadError(t('An error occurred while uploading the image.'));

  // The uploader module is configured once at construction: its handler reads
  // the current props through refs.
  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const onPendingImageDeltaRef = useRef(onPendingImageDelta);
  onPendingImageDeltaRef.current = onPendingImageDelta;

  const insertImagesAt = (editor: Quill, index: number, files: File[]) => {
    const upload = onImageUploadRef.current;
    if (disabledRef.current || upload == null) {
      return;
    }
    insertImages(editor, index, files, {
      upload,
      onError: showUploadError,
      onPendingChange: delta => onPendingImageDeltaRef.current?.(delta),
    });
  };
  const insertImagesAtRef = useRef(insertImagesAt);
  insertImagesAtRef.current = insertImagesAt;

  const allowsImages = onImageUpload != null || disabled;
  const { editorRef, quill, isBlank, editorKey } = useQuillEditor({
    value: value.getHtml(),
    onChange: handleChange,
    serialize: editor => canonicalHtmlFromQuill(editor, LINE_BLOCK_EMBEDS),
    seedEditor,
    formats: allowsImages ? EDITOR_FORMATS : EDITOR_FORMATS_WITHOUT_IMAGES,
    modules: {
      // Image drop AND paste go to the hosted-upload pipeline instead of
      // Quill's default base64 embed; without a pipeline, nothing is inserted.
      uploader: {
        handler(
          this: { quill: Quill },
          range: { index: number; length: number },
          files: File[],
        ) {
          if (disabledRef.current || onImageUploadRef.current == null) {
            return;
          }
          // Paste over a selection replaces it, like Quill's default handler.
          if (range.length > 0) {
            this.quill.deleteText(range.index, range.length, 'user');
          }
          insertImagesAtRef.current(this.quill, range.index, files);
        },
      },
      keyboard: singleLine
        ? {
            bindings: {
              singleLineEnter: { key: 'Enter', handler: () => false },
              singleLineShiftEnter: {
                key: 'Enter',
                shiftKey: true,
                handler: () => false,
              },
            },
          }
        : undefined,
    },
    readOnly: disabled,
    matchers: QUILL_MATCHERS,
    onTextChange: handleTextChange,
    isEmpty: isEditorBlank,
    configureEditor,
    autoFocus,
    remountDeps: [disabled, singleLine, allowsImages],
  });

  useEffect(() => {
    if (quill == null) {
      return;
    }
    refreshPreviews(quill);
  }, [quill, refreshPreviews, snippetPreviewLanguage, snippetPreviewT]);

  useEffect(() => {
    if (quill == null) {
      return;
    }
    const onSelectionChange = () => moveCaretOutOfChip(quill);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [quill]);

  return (
    <div
      className={className}
      css={[
        // No default height: only the two composers fill their host (they
        // pass `height: '100%'` through `css`, as `variant="filled"` did
        // before the restyle); every other call site hugs its content, so a
        // flex/grid parent with a definite height must not stretch it.
        qlEditorSurfaceStyles,
        {
          '.ql-editor': {
            ...qlEditorBaseStyles,
            outline: 'none',
            minHeight: singleLine ? undefined : 80,
            padding: singleLine ? '8.5px 14px' : '8px 16px',
            fontSize: singleLine ? '0.875rem' : undefined,
            opacity: disabled ? 0.5 : 1,
            ...(pinSignatureToBottom && {
              display: 'flex',
              flexDirection: 'column',
            }),
          },
          '@supports (-webkit-touch-callout: none)': {
            '.ql-editor': singleLine
              ? {
                  fontSize: '0.875rem',
                }
              : {
                  fontSize: '16px',
                  transform: 'scale(0.92)',
                  width: '108%',
                  transformOrigin: 'top left',
                },
          },
          '.ql-editor p': {
            padding: 0,
            margin: 0,
          },
          ...qlListStyles,
          // Shrink-wraps the card so the remove button anchors to its corner
          // and not to the editor's right edge on a wide composer.
          '.ra-snippet': {
            position: 'relative',
            width: 'fit-content',
            maxWidth: '100%',
          },
          [`.${SNIPPET_REMOVE_CLASS}`]: {
            position: 'absolute',
            top: 6,
            right: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            padding: 0,
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            color: muiTheme.palette.common.white,
            background: 'rgba(0, 0, 0, 0.54)',
            opacity: 0,
            transition: 'opacity 120ms',
            '&:hover': { background: 'rgba(0, 0, 0, 0.74)' },
            '&:focus-visible': { opacity: 1 },
          },
          [`.ra-snippet:hover .${SNIPPET_REMOVE_CLASS}`]: { opacity: 1 },
          '.ra-var': {
            background: '#e8f0fe',
            borderRadius: 3,
            padding: '0 4px',
            color: customPalette.blue500,
          },
          '.ra-var[data-ra-var-empty]::before': {
            content: 'attr(data-ra-var)',
            opacity: 0.6,
          },
          '.ra-image-placeholder': {
            position: 'relative',
            display: 'inline-block',
            width: 120,
            height: 90,
            verticalAlign: 'bottom',
            borderRadius: 4,
            backgroundColor: 'var(--mui-palette-action-hover)',
            border: '1px solid var(--mui-palette-divider)',
          },
          '.ra-image-placeholder::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 20,
            height: 20,
            marginTop: -10,
            marginLeft: -10,
            borderRadius: '50%',
            border: '2px solid var(--mui-palette-divider)',
            borderTopColor: 'var(--mui-palette-text-secondary)',
            animation: 'ra-image-placeholder-spin 0.8s linear infinite',
          },
          '@keyframes ra-image-placeholder-spin': {
            to: { transform: 'rotate(360deg)' },
          },
          '.ra-custom-html-pill': {
            background: '#efefef',
            borderRadius: 2,
            fontWeight: 700,
            display: 'inline-block',
            padding: '0 4px',
            cursor: 'pointer',
          },
          '.ra-custom-html-body blockquote': {
            marginLeft: 10,
            borderLeft: '2px solid #efefef',
            paddingLeft: 10,
          },
          '.ra-signature': {
            marginTop: pinSignatureToBottom ? 'auto' : 0,
            paddingTop: '28px',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '15px',
              left: 0,
              right: 0,
              borderTop: '1px solid var(--mui-palette-divider)',
            },
            color: 'var(--mui-palette-text-primary)',
            fontSize: '0.875rem',
            '*[style*="color:"]': {
              color: 'var(--mui-palette-text-primary) !important',
            },
          },
          '.ql-placeholder, .ql-editor.ql-blank::before': {
            color: muiTheme.palette.grey[500],
            fontStyle: 'normal',
          },
          figure: { margin: 0 },
        },
        css,
      ]}
    >
      {!singleLine && (
        <Toolbar
          quill={quill}
          disabled={disabled}
          onPickImage={
            onImageUpload == null
              ? undefined
              : file => {
                  if (quill == null) {
                    return;
                  }
                  const range = quill.getSelection();
                  insertImagesAt(
                    quill,
                    range != null ? range.index : quill.getLength(),
                    [file],
                  );
                }
          }
          showSnippetButton={showSnippetButton}
          snippetLotId={snippetLotId}
          snippetDevelopmentId={snippetDevelopmentId}
          presetSnippet={presetSnippet}
        />
      )}
      <div css={{ position: 'relative' }} onClick={() => quill?.focus()}>
        <div key={editorKey} ref={editorRef} />
        {placeholder != null && isBlank && (
          <div
            css={{
              color: muiTheme.palette.grey[500],
              position: 'absolute',
              top: 0,
              left: 0,
              padding: '8px 16px',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            {placeholder}
          </div>
        )}
      </div>
      {suggestions != null && !disabled && (
        <MentionDropdown quill={quill} suggestions={suggestions} />
      )}
      {imageUploadError != null && (
        <RASnackbar
          message={imageUploadError}
          severity="error"
          onClose={() => setImageUploadError(null)}
        />
      )}
    </div>
  );
};
